import { BaseNodeModel } from "@/models/nodes/BaseNodeModel";

import { InlineNodeModel } from "./InlineNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeId, NodeModel } from "@/models/nodes/types";
import type { VisitableNode, NodeVisitor } from "@/models/nodes/visitors/NodeVisitor";
import type { ComputedResultNodeProps, ComputedResultNodeConstructorProps } from "@corca/data";

export class ComputedResultNodeModel
  extends BaseNodeModel<ComputedResultNodeProps>
  implements VisitableNode
{
  static type = "computedResult" as const;

  type = ComputedResultNodeModel.type;

  readonly isAtom = true;

  protected fillRequiredProps(props: ComputedResultNodeConstructorProps): ComputedResultNodeProps {
    return props;
  }

  static create(
    doc: DocumentModel,
    props: ComputedResultNodeProps = {},
    nodes: InlineNodeModel | NodeModel[] = [],
    id?: NodeId,
  ) {
    const inline = Array.isArray(nodes) ? doc.create(InlineNodeModel, nodes) : nodes;
    return new ComputedResultNodeModel(doc, id, { ...props, inlineId: inline.id }, [inline]);
  }

  /**
   * The inline node holding the current value, resolved via the `inlineId`
   * last-writer-wins pointer. A concurrent write may briefly leave a losing
   * inline node in `children`; `inlineId` names the winner, and the heal pass
   * drops the rest. Falls back to the first inline child when no pointer is set.
   */
  get inlineNode(): InlineNodeModel | null {
    const { inlineId } = this.props;
    const pointed = inlineId ? this.children.find((n) => n.id === inlineId) : undefined;
    const node = pointed ?? this.children.find((n) => n.is(InlineNodeModel));
    return node?.is(InlineNodeModel) ? node : null;
  }

  accept<R>(visitor: NodeVisitor<R>): R {
    return visitor.visitComputedResultNode(this);
  }
}
